import moment from 'moment';
import {DateFormats, FLOAT, formatDate, INTEGER, stringToNumber} from '@pb/kit-formatters';

import {sanitizeValue} from './sanitize-value';
import {ERROR_TYPES, MAX_INT_32_NUMBER, MAX_NUMBER, MAX_SAFE_INTEGER, MAX_YEAR, MIN_YEAR, ValidType} from './constants';
import {convertNumberToDate} from './convert-number-to-date';
import {onlyDigit} from './only-digit';
import {formattedYear} from './formatted-year';
import {formatDateRange} from '../../data-typed-input/utils/formatDateRange';

import type {Options} from '@pb/kit-formatters';

import type {UserLocaleDto, ValidTypes} from './constants';

const FLOAT_DECIMALS = 2 as const;
const MAX_SAFE_FLOAT = MAX_SAFE_INTEGER / 10 ** FLOAT_DECIMALS;

export const formattedValue = (
    value: string,
    type: ValidTypes,
    locale: UserLocaleDto,
    options?: Options,
): {
    value: string,
    errorType?: keyof typeof ERROR_TYPES,

} => {
    switch (type) {
        case ValidType.NUMBER:
        case ValidType.NUMBER_POSITIVE:
        case ValidType.INT:
        case ValidType.INT_POSITIVE:
        case ValidType.MAX_SAFE_NUMBER:
        case ValidType.MAX_SAFE_NUMBER_POSITIVE:
            if (value.length > 0) {
                const sanitizedValue = sanitizeValue(value);
                const numberValue = stringToNumber(sanitizedValue, locale);
                let numberFormatter = FLOAT;
                const isNotPositiveValid = numberValue < 0 && (
                    type === ValidType.NUMBER_POSITIVE
                    || type === ValidType.MAX_SAFE_NUMBER_POSITIVE
                    || type === ValidType.INT_POSITIVE
                );
                const isMaxSafeType = type === ValidType.MAX_SAFE_NUMBER
                    || type === ValidType.MAX_SAFE_NUMBER_POSITIVE;
                const isNotSafeValid =
                    isMaxSafeType &&
                    (numberValue > MAX_SAFE_INTEGER ||
                        numberValue < -MAX_SAFE_INTEGER);

                if (
                    !sanitizedValue.length ||
                    isNotPositiveValid ||
                    isNotSafeValid ||
                    numberValue > MAX_NUMBER ||
                    isNaN(numberValue)
                ) {
                    return {
                        value: '',
                        errorType: ERROR_TYPES.MAX_NUMBER_ERROR,
                    };
                }

                if (isMaxSafeType) {
                    numberFormatter =
                        MAX_SAFE_FLOAT < numberValue ||
                        -MAX_SAFE_FLOAT > numberValue
                            ? INTEGER
                            : FLOAT;
                }

                return {
                    value: numberFormatter.formatNumber(numberValue, locale, {
                        hideTrailingZeros: true,
                        ...options,
                    }),
                };
            }

            return {
                value,
            };


        case ValidType.INT_NUMBER_0_TO_100:
        case ValidType.NUMBER_0_TO_100: {
            const numberValue = stringToNumber(value, locale);

            if (numberValue < 0 || numberValue > 100 || isNaN(numberValue)) {
                return {
                    value: '',
                    errorType: ERROR_TYPES.WRONG_NUMBER_VALUE,
                };
            }

            return {
                value,
            };
        }

        case ValidType.INT_32: {
            const numberValue = stringToNumber(value, locale);

            if (
                numberValue < 0 ||
                numberValue > MAX_INT_32_NUMBER ||
                isNaN(numberValue)
            ) {
                return {
                    value: '',
                    errorType: ERROR_TYPES.MAX_NUMBER_ERROR,
                };
            }

            return {
                value,
            };
        }

        case ValidType.YEAR:
            if (value.length === 0) {
                return {
                    value,
                };
            }

            const format = 'YYYY';
            const formatted = moment(formattedYear(value), format);

            if (
                !onlyDigit(value) ||
                formatted.year() < MIN_YEAR ||
                formatted.year() > MAX_YEAR
            ) {
                return {
                    errorType: ERROR_TYPES.WRONG_YEAR,
                    value: '',
                };
            }

            return {
                value: formatDate(formatted, format),
            };

        case ValidType.DATE:
            if (value.length === 0) {
                return {
                    value,
                };
            }

            const dateFormat = DateFormats[locale.dateFormat] || locale.dateFormat;
            const date = convertNumberToDate(value, dateFormat);

            if (!date.isValid()) {
                const invalidIndex = date.invalidAt();
                let errorType;

                if (invalidIndex === 1) {
                    errorType = ERROR_TYPES.WRONG_MONTH;
                } else {
                    errorType =
                        invalidIndex === 0
                            ? ERROR_TYPES.WRONG_YEAR
                            : ERROR_TYPES.WRONG_DATE;
                }

                return {
                    errorType,
                    value: '',
                };
            } else if (date.year() < MIN_YEAR || date.year() > MAX_YEAR) {
                return {
                    errorType: ERROR_TYPES.WRONG_YEAR,
                    value: '',
                };
            }

            return {
                value: formatDate(date, dateFormat),
            };

        case ValidType.DATE_RANGE: {
            if (value.length === 0) {
                return {value};
            }

            const validateDateCallback =
                (momentDate: ReturnType<typeof moment>): {errorType?: keyof typeof ERROR_TYPES} => {
                    if (!momentDate.isValid()) {
                        const invalidIndex = momentDate.invalidAt();

                        if (invalidIndex === 1) {
                            return {errorType: ERROR_TYPES.WRONG_MONTH};
                        } else {
                            return {errorType: ERROR_TYPES.INVALID_DATE_RANGE};
                        }
                    } else if (momentDate.year() < MIN_YEAR || momentDate.year() > MAX_YEAR) {
                        return {errorType: ERROR_TYPES.WRONG_YEAR};
                    }
                    return {errorType: undefined};
                };

            const formattedRange = formatDateRange(value, locale.dateFormat, validateDateCallback);

            if (formattedRange?.errorType) {
                return {
                    errorType: formattedRange.errorType,
                    value,
                };
            }

            return {
                value: formattedRange?.value ?? '',
            };
        }


        case ValidType.PHONE:
            return {
                value,
            };

        case ValidType.HTTP_PORT:
            const numberValue = Number(value);

            if (isNaN(numberValue) || numberValue < 0) {
                return {
                    value: '',
                    errorType: ERROR_TYPES.WRONG_HTTP_PORT,
                };
            }

            return {
                value,
            };
    }

    return {
        value: '',
    };
};
